/**
 * @author Mr_FabiozZz[mr.fabiozzz@gmail.com]
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { $api } from "libs/axios";

interface IPaginate<T> {
	link: string;
	params?: { [key: string]: any };
	then: (arr: T[]) => void;
	ref?: null | React.MutableRefObject<any>;
	lastTarget: React.MutableRefObject<any>;
	dependencies?: any[];
	catchHandler?: () => void;
	margin?: string;
	threshold?: number;
}

export function usePaginationWithIntersection<T>({
	link,
	params = {},
	then,
	ref,
	lastTarget,
	dependencies = [],
	catchHandler = () => {},
	margin: rootMargin = "40px",
	threshold = 0.1,
}: IPaginate<T>) {
	const [fetch, setFetch] = useState(true);

	const [isFinally, setIsFinally] = useState(false);

	const firstLoad = useRef(false);

	let observer: IntersectionObserver;

	const submit = useCallback(() => {
		$api
			.get(link, { params })
			.then(({ data }) => then(data))
			.catch(catchHandler)
			.finally(() => setFetch(false));
	}, [link, params, then, catchHandler]);

	useEffect(() => {
		if (fetch && !isFinally) {
			if (dependencies.length) {
				const flag = dependencies.every((_) => !!_);
				if (flag) {
					submit();
				}
			} else {
				submit();
			}
		}
		//eslint-disable-next-line
	}, [fetch, params, ...dependencies, isFinally]);

	useEffect(() => {
		// if (ref.current) {
		if (lastTarget.current) {
			const interceptConfig = {
				root: ref === null ? null : ref!.current,
				rootMargin,
				threshold,
			};
			// eslint-disable-next-line react-hooks/exhaustive-deps
			observer = new IntersectionObserver((entries) => {
				if (entries.every((entry) => entry.isIntersecting)) {
					setFetch(true);
					if (firstLoad.current) {
						if (fetch && !isFinally) {
							setFetch(true);
						}
					} else {
						firstLoad.current = true;
					}
				}
			}, interceptConfig);
			observer.observe(lastTarget.current);
			return () => {
				observer.disconnect();
			};
		}
		// }
	}, [ref, lastTarget.current, params, isFinally, fetch]);
	return {
		fetch,
		setFetch,
		setIsFinally,
	};
}
